import { connect, disconnect, subscribe } from "./client";
import { useMessageStore } from "@/modules/message/store";
import { useConversationStore } from "@/modules/conversation/store";
import { useAccountStore } from "@/modules/account/store";
import { listAccounts } from "@/modules/account/api";
import { getPendingInvitations } from "@/modules/account/invitations-api";
import { useTypingStore } from "@/modules/conversation/typing-store";
import { useAuthStore } from "@/modules/auth/store";
import { useCallStore } from "@/modules/call/store";
import { getNotificationsEnabled } from "@/lib/settings";
import { readActiveAccountId } from "@/modules/account/persistence";
import { chatUrl } from "@/lib/chat-urls";
import type { Message } from "@/modules/message/types";
import type { Conversation } from "@/modules/conversation/types";
import type { Subscription } from "centrifuge";

let conversationSub: Subscription | null = null;
let userSub: Subscription | null = null;
let activeConversationId: string | null = null;

function shouldShowSystemNotification(force = false) {
  if (typeof window === "undefined") return false;
  if (!force && !getNotificationsEnabled()) return false;
  return document.hidden || !document.hasFocus();
}

function showBrowserNotification({
  title,
  body,
  tag,
  onClick,
}: {
  title: string;
  body: string;
  tag: string;
  onClick: () => void;
}) {
  if (typeof window === "undefined") return;
  if (!("Notification" in window) || Notification.permission !== "granted") return;
  const notification = new Notification(title, { body, tag });
  notification.onclick = () => {
    window.focus();
    onClick();
  };
}

export function setActiveConversation(id: string | null) {
  activeConversationId = id;
}

export function initRealtime() {
  connect();
}

export function cleanupRealtime() {
  unsubscribeConversation();
  unsubscribeUser();
  disconnect();
}

export function subscribeCurrentUser(userId: string) {
  unsubscribeUser();

  const refreshAccountState = async () => {
    const [accounts, pendingInvitations] = await Promise.all([
      listAccounts().catch(() => []),
      getPendingInvitations().catch(() => []),
    ]);
    const accountStore = useAccountStore.getState();
    accountStore.setAccounts(accounts);
    accountStore.setPendingInvitations(pendingInvitations);

    const saved = readActiveAccountId();
    const nextActiveId =
      (saved && accounts.find((account) => account.id === saved)?.id) ||
      (accountStore.activeAccountId &&
        accounts.find((account) => account.id === accountStore.activeAccountId)?.id) ||
      accounts[0]?.id ||
      null;

    accountStore.setActiveAccountId(nextActiveId);
  };

  userSub = subscribe(`user:user:${userId}`, (data) => {
    const event = data as {
      type: string;
      user?: {
        id: string;
        first_name: string;
        last_name: string;
        avatar_url?: string | null;
        email?: string | null;
        phone?: string | null;
        is_system_admin?: boolean;
      };
      conversation?: Conversation;
      conversation_id?: string;
      account_id?: string;
      last_read_message_id?: string;
      user_id?: string;
      message_id?: string;
      sender_id?: string;
      sender_name?: string;
      conversation_title?: string | null;
      text?: string;
      // Call events
      call_id?: string;
      caller_id?: string;
      caller_name?: string;
    };

    const clearActiveCallSnapshot = (conversationId?: string, callId?: string) => {
      const store = useConversationStore.getState();
      const byConversationId = conversationId
        ? store.conversations.find((conversation) => conversation.id === conversationId)
        : null;
      const targetConversation = byConversationId
        ?? (callId
          ? store.conversations.find((conversation) => conversation.active_call?.id === callId)
          : null);
      if (targetConversation) {
        store.updateConversation({
          ...targetConversation,
          active_call: null,
        });
      }
    };

    if (event.type === "profile_updated" && event.user) {
      const current = useAuthStore.getState().user;
      if (!current || current.id !== event.user.id) return;
      useAuthStore.getState().setUser({
        id: event.user.id,
        first_name: event.user.first_name,
        last_name: event.user.last_name,
        avatar_url: event.user.avatar_url ?? null,
        email: event.user.email ?? current.email,
        phone: event.user.phone ?? current.phone,
        is_system_admin: !!event.user.is_system_admin,
        status: current.status || "active",
      });
    }

    if (event.type === "new_conversation" && event.conversation) {
      useConversationStore.getState().addConversation(event.conversation);
    }

    if (event.type === "accounts_updated") {
      void refreshAccountState();
    }

    if (event.type === "new_message_notification" && event.conversation_id) {
      const activeAccountId = useAccountStore.getState().activeAccountId;
      const messageAccountId = event.account_id;
      const isDifferentAccount = messageAccountId && messageAccountId !== activeAccountId;
      const isViewingChat = activeConversationId === event.conversation_id;

      console.log("[notify]", {
        activeConversationId,
        eventConversationId: event.conversation_id,
        isViewingChat,
        activeAccountId,
        messageAccountId,
        isDifferentAccount,
      });

      if (isDifferentAccount) {
        useConversationStore.getState().incrementCrossAccountUnread(messageAccountId);
      }

      if (!isViewingChat) {
        useConversationStore.getState().incrementUnread(event.conversation_id);
      }

      // Update last message in conversation list
      if (event.text || event.sender_name) {
        useConversationStore.getState().updateLastMessage(
          event.conversation_id,
          new Date().toISOString(),
          event.text,
          event.sender_id,
          event.sender_name,
        );
      }

      // Show notification if:
      // - Window not focused (background/minimized)
      // - OR user is viewing a different conversation
      // - OR user is in a different account
      const isViewingThisChat = activeConversationId === event.conversation_id;
      const shouldNotify = !isViewingThisChat && getNotificationsEnabled();

      if (shouldNotify) {
        const title = event.conversation_title
          ? event.conversation_title
          : (event.sender_name || "Meridian Chat");
        const body = event.conversation_title && event.sender_name
          ? `${event.sender_name}: ${event.text || ""}`
          : (event.text || "Новое сообщение");
        const conversationId = event.conversation_id;
        const electronAPI = (window as any).electronAPI;

        if (electronAPI?.app?.showNotification) {
          electronAPI.app.showNotification({ title, body, conversationId });
        } else if ("Notification" in window && Notification.permission === "granted") {
          const n = new Notification(title, { body, tag: conversationId });
          n.onclick = () => {
            window.focus();
            const acc = useAccountStore.getState().activeAccount();
            window.location.href = acc?.public_id
              ? chatUrl(acc.public_id, conversationId)
              : `/chats/${conversationId}`;
          };
        }
      }
    }

    if (
      event.type === "conversation_read" &&
      event.conversation_id &&
      event.user_id === userId &&
      event.last_read_message_id
    ) {
      useConversationStore.getState().clearUnread(event.conversation_id);
    }

    // Call events
    // Backend sends call_type as a separate field (since "type" is the event name)
    if (event.type === "incoming_call" && event.call_id && event.conversation_id && event.caller_id) {
      const rawType = (event as Record<string, unknown>).call_type ?? (event as Record<string, unknown>).type;
      const callType = rawType === "video" ? "video" as const : "audio" as const;
      useCallStore.getState().setIncomingCall({
        callId: event.call_id,
        conversationId: event.conversation_id,
        type: callType,
        callerId: event.caller_id,
        callerName: event.caller_name || "",
      });

      // Desktop notification for incoming calls
      const electronAPI = (window as any).electronAPI;
      if (electronAPI?.app?.showNotification && shouldShowSystemNotification(true)) {
        electronAPI.app.showNotification({
          title: callType === "video" ? "Входящий видеозвонок" : "Входящий звонок",
          body: event.caller_name || "",
          conversationId: event.conversation_id,
        });
      } else if (shouldShowSystemNotification(true)) {
        showBrowserNotification({
          title: callType === "video" ? "Incoming video call" : "Incoming call",
          body: event.caller_name || "Meridian Chat",
          tag: `call:${event.call_id}`,
          onClick: () => {
            const convId = event.conversation_id!;
            const acc = useAccountStore.getState().activeAccount();
            window.location.href = acc?.public_id
              ? chatUrl(acc.public_id, convId)
              : `/chats/${convId}`;
          },
        });
      }
    }

    if (event.type === "call_accepted" && event.call_id) {
      const active = useCallStore.getState().activeCall;
      const incoming = useCallStore.getState().incomingCall;
      if (active && active.callId === event.call_id) {
        useCallStore.getState().setActiveCall({ ...active, status: "active" });
      }
      if (incoming && incoming.callId === event.call_id) {
        useCallStore.getState().setIncomingCall(null);
      }
    }

    if (event.type === "call_presence_updated" && event.conversation_id) {
      const callSnapshot = (event as Record<string, unknown>).call as {
        id?: string;
        status?: string;
      } | null | undefined;
      const existingConversation = useConversationStore
        .getState()
        .conversations.find((conversation) => conversation.id === event.conversation_id);
      if (existingConversation) {
        useConversationStore.getState().updateConversation({
          ...existingConversation,
          active_call: callSnapshot as any ?? null,
        });
      }

      const incoming = useCallStore.getState().incomingCall;
      if (
        incoming &&
        incoming.conversationId === event.conversation_id &&
        callSnapshot?.id === incoming.callId &&
        callSnapshot.status === "active"
      ) {
        useCallStore.getState().setIncomingCall(null);
      }
    }

    if (event.type === "call_declined" && event.call_id) {
      const incoming = useCallStore.getState().incomingCall;
      if (incoming && incoming.callId === event.call_id) {
        useCallStore.getState().setIncomingCall(null);
      }
    }

    if (event.type === "call_ended" && event.call_id) {
      const active = useCallStore.getState().activeCall;
      const incoming = useCallStore.getState().incomingCall;
      if (active && active.callId === event.call_id) {
        useCallStore.getState().clearCall();
      }
      if (incoming && incoming.callId === event.call_id) {
        useCallStore.getState().setIncomingCall(null);
      }
      clearActiveCallSnapshot(event.conversation_id, event.call_id);
    }
  });
}

export function subscribeConversation(conversationId: string) {
  unsubscribeConversation();

  conversationSub = subscribe(`chat:chat:${conversationId}`, (data) => {
    const event = data as {
      type: string;
      message?: Message;
      message_id?: string;
      user_id?: string;
      user_name?: string | null;
      last_message_text?: string | null;
      last_message_sender_id?: string | null;
      last_message_sender_name?: string | null;
      last_read_message_id?: string;
      reactions?: Array<{ id: string; user_id: string; emoji: string }>;
      call_id?: string;
    };

    if (event.type === "new_message" && event.message) {
      useMessageStore.getState().addMessage(conversationId, event.message);
      // Update last message in sidebar (for the active conversation channel)
      useConversationStore.getState().updateLastMessage(
        conversationId,
        event.message.created_at,
        event.last_message_text ?? event.message.content,
        event.last_message_sender_id ?? event.message.sender_id,
        event.last_message_sender_name ?? null,
      );
    }

    if ((event.type === "edit_message" || event.type === "attachment_ready") && event.message) {
      useMessageStore.getState().updateMessagePreservingLocalUploads(conversationId, event.message);
    }

    if (event.type === "delete_message" && event.message_id) {
      useMessageStore.getState().removeMessage(conversationId, event.message_id);
    }

    if (event.type === "user_typing" && event.user_id) {
      const currentUserId = useAuthStore.getState().user?.id;
      if (!currentUserId || event.user_id === currentUserId) return;
      const store = useTypingStore.getState();
      store.setTyping(conversationId, event.user_id, event.user_name);
      // Auto-clear after 3 seconds
      setTimeout(() => {
        useTypingStore.getState().clearTyping(conversationId, event.user_id!);
      }, 3000);
    }

    if (event.type === "reaction_update" && event.message_id && event.reactions) {
      useMessageStore.getState().updateReactions(conversationId, event.message_id, event.reactions);
    }

    if (event.type === "conversation_read" && event.user_id && event.last_read_message_id) {
      useMessageStore.getState().setReadReceipt(
        conversationId,
        event.user_id,
        event.last_read_message_id,
      );
    }

    if (event.type === "call_presence_updated" && activeConversationId === conversationId) {
      const payload = event as Record<string, unknown>;
      const existingConversation = useConversationStore
        .getState()
        .conversations.find((conversation) => conversation.id === conversationId);
      if (existingConversation) {
        useConversationStore.getState().updateConversation({
          ...existingConversation,
          active_call: (payload.call as any) ?? null,
        });
      }
    }

    if (event.type === "call_ended") {
      const existingConversation = useConversationStore
        .getState()
        .conversations.find((conversation) => conversation.id === conversationId);
      if (existingConversation && (!event.call_id || existingConversation.active_call?.id === event.call_id)) {
        useConversationStore.getState().updateConversation({
          ...existingConversation,
          active_call: null,
        });
      }
    }
  });
}

export function unsubscribeConversation() {
  if (conversationSub) {
    conversationSub.unsubscribe();
    conversationSub = null;
  }
}

export function unsubscribeUser() {
  if (userSub) {
    userSub.unsubscribe();
    userSub = null;
  }
}
